import { authState } from '../../lib/auth.js';
import { getBackendUrl } from '../../lib/backend.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';
import { css, html } from '../../vendor/lit.min.js';
import '../login-form.js';

const USER_CODE_KEY = 'd3auth_device_user_code';

// Device Authorization Grant verification page (RFC 8628 §3.3). A signed-in
// user lands here from the verification_uri shown on their device, sees which
// app is asking, and approves or denies it.
class DeviceVerifyView extends BaseComponent {
    static properties = {
        userCode: { type: String, state: true },
        appName: { type: String, state: true },
        scopes: { type: Array, state: true },
        // input | loading | confirm | approved | denied
        status: { type: String, state: true },
        error: { type: String, state: true },
        loading: { type: Boolean, state: true },
        authed: { type: Boolean, state: true },
    };

    static styles = css`
        :host { display: block; }
        .device-card { max-width: 460px; margin: 0 auto; }
        .user-code {
            font-size: 1.75rem;
            letter-spacing: 0.4rem;
            text-align: center;
            font-family: monospace;
        }
    `;

    constructor() {
        super();
        this.userCode = '';
        this.appName = '';
        this.scopes = [];
        this.status = 'input';
        this.error = '';
        this.loading = false;
        this.authed = false;
    }

    async connectedCallback() {
        super.connectedCallback();
        const query = router.getQuery();
        // The user_code comes from the verification link's query string. Persist
        // it so it survives a full-page Entra sign-in redirect (MSAL restores the
        // original URL, but sessionStorage covers the edge cases), and fall back
        // to it if the query is empty on return.
        this.userCode = (
            query.get('user_code') ||
            sessionStorage.getItem(USER_CODE_KEY) ||
            ''
        ).trim();
        if (this.userCode) sessionStorage.setItem(USER_CODE_KEY, this.userCode);

        this.authed = authState.isAuthenticated;
        if (!this.authed) {
            // Sign-in happens inline via <login-form>; resume once authenticated
            // (here after an inline code sign-in, on the Entra redirect return, or
            // if the user signs in in another tab).
            this._unsub = authState.subscribe((state) => {
                if (!state.loading && state.isAuthenticated && !this.authed) {
                    this.authed = true;
                    if (this.userCode) this._loadInfo();
                }
            });
            return;
        }
        if (this.userCode) this._loadInfo();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._unsub) this._unsub();
    }

    async _loadInfo() {
        // Consumed — clear the redirect-bridge copy so a later reload without a
        // user_code doesn't pick up a stale one.
        sessionStorage.removeItem(USER_CODE_KEY);
        this.status = 'loading';
        this.error = '';
        try {
            const token = authState.getAccessToken();
            const res = await fetch(
                `${getBackendUrl()}/api/v1/device?user_code=${encodeURIComponent(this.userCode)}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );
            const data = await res.json();
            if (!res.ok) {
                this.error =
                    data.error === 'not_found'
                        ? 'That code is invalid or has expired.'
                        : 'Could not look up that code.';
                this.status = 'input';
                return;
            }
            this.appName = data.app_name || data.client_id || 'An application';
            this.scopes = data.scopes || [];
            if (data.status === 'approved') {
                this.status = 'approved';
                return;
            }
            if (data.status === 'denied') {
                this.status = 'denied';
                return;
            }
            this.status = 'confirm';
        } catch (err) {
            this.error = err.message;
            this.status = 'input';
        }
    }

    async _decide(approve) {
        this.loading = true;
        this.error = '';
        try {
            const token = authState.getAccessToken();
            const res = await fetch(`${getBackendUrl()}/api/v1/device`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ user_code: this.userCode, approve }),
            });
            const data = await res.json();
            if (!res.ok) {
                this.error =
                    data.error === 'already_decided'
                        ? 'This request was already handled.'
                        : data.error_description || 'Something went wrong.';
                this.loading = false;
                return;
            }
            this.status = data.status === 'approved' ? 'approved' : 'denied';
        } catch (err) {
            this.error = err.message;
        } finally {
            this.loading = false;
        }
    }

    _submitCode(e) {
        e.preventDefault();
        this.userCode = this.userCode.trim();
        if (this.userCode) this._loadInfo();
    }

    render() {
        return html`
            <div class="device-card mt-5">
                <div class="card shadow">
                    <div class="card-body p-4">
                        <h3 class="card-title text-center mb-4">Device sign-in</h3>
                        ${this.error ? html`<div class="alert alert-danger">${this.error}</div>` : ''}
                        ${this._renderBody()}
                    </div>
                </div>
            </div>
        `;
    }

    _renderBody() {
        if (!this.authed) {
            return html`
                <p class="text-muted text-center">Sign in to approve this device.</p>
                <login-form></login-form>
            `;
        }
        if (this.status === 'loading') {
            return html`<div class="text-center"><div class="spinner-border text-primary" role="status"></div></div>`;
        }
        if (this.status === 'approved') {
            return html`<div class="alert alert-success">Approved — you can return to your device.</div>`;
        }
        if (this.status === 'denied') {
            return html`<div class="alert alert-secondary">Request denied. You can close this page.</div>`;
        }
        if (this.status === 'confirm') {
            return html`
                <p class="text-center"><strong>${this.appName}</strong> wants to sign in to your account.</p>
                <p class="user-code">${this.userCode}</p>
                ${this.scopes.length ? html`<p class="text-muted small">Access: ${this.scopes.join(', ')}</p>` : ''}
                <div class="d-flex gap-2">
                    <button class="btn btn-outline-secondary w-100" ?disabled="${this.loading}" @click="${() => this._decide(false)}">Deny</button>
                    <button class="btn btn-primary w-100" ?disabled="${this.loading}" @click="${() => this._decide(true)}">Approve</button>
                </div>
            `;
        }
        return html`
            <p class="text-muted text-center">Enter the code shown on your device.</p>
            <form @submit="${this._submitCode}">
                <input type="text" class="form-control user-code mb-3" autofocus
                    .value="${this.userCode}"
                    @input="${(e) => (this.userCode = e.target.value.toUpperCase())}">
                <button type="submit" class="btn btn-primary w-100" ?disabled="${!this.userCode}">Continue</button>
            </form>
        `;
    }
}

customElements.define('device-verify-view', DeviceVerifyView);
export { DeviceVerifyView };
